import { Stack, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import {
  ApiWorkflowDialog,
  type ApiWorkflowDialogMessages,
  DialogMessageBox,
} from '@verantos-dev/shared-components';

import { useDeleteAnalyticalDatasetDialog } from '../hooks/useDeleteAnalyticalDatasetDialog';

interface DeleteAnalyticalDatasetDialogProps {
  projectId: string;
  analyticalDatasetProfileId: string;
  name: string;
  onClose: () => void;
}

/**
 * Inline delete-confirmation dialog opened from an analytical-dataset row on the
 * artifacts page. Lives here (not reused from feature-analytical-datasets) to
 * avoid a dependency cycle. Reuses the `analyticalDatasetsList.deleteModal.*`
 * message ids — registered app-wide via `analyticalDatasetsMessageKeysEn` — so
 * copy stays in sync with the standalone modal without importing the feature lib.
 */
export const DeleteAnalyticalDatasetDialog = ({
  projectId,
  analyticalDatasetProfileId,
  name,
  onClose,
}: DeleteAnalyticalDatasetDialogProps) => {
  const { formatMessage } = useIntl();
  const { onSubmit, closeDialog, isPending, submittingError, isValidToSubmit } =
    useDeleteAnalyticalDatasetDialog({ projectId, analyticalDatasetProfileId, onClose });

  const dialogMessages: ApiWorkflowDialogMessages = {
    entryCancelButtonText: formatMessage({ id: 'analyticalDatasetsList.deleteModal.cancelButton' }),
    entrySaveButtonText: formatMessage({ id: 'analyticalDatasetsList.deleteModal.deleteButton' }),
    submittingCancelButtonText: formatMessage({
      id: 'analyticalDatasetsList.deleteModal.cancelButton',
    }),
    submittingSaveButtonText: formatMessage({
      id: 'analyticalDatasetsList.deleteModal.submitting',
    }),
    successCloseButtonText: formatMessage({ id: 'analyticalDatasetsList.deleteModal.closeButton' }),
    submittingErrorTitle: formatMessage({
      id: 'analyticalDatasetsList.deleteModal.submittingError',
    }),
    successTitle: formatMessage({ id: 'analyticalDatasetsList.deleteModal.success.title' }),
    successText: formatMessage({ id: 'analyticalDatasetsList.deleteModal.success.text' }, { name }),
  };

  return (
    <ApiWorkflowDialog
      id="deleteAnalyticalDatasetDialog"
      title={formatMessage({ id: 'analyticalDatasetsList.deleteModal.title' })}
      dialogMessages={dialogMessages}
      onClose={closeDialog}
      onSubmit={onSubmit}
      saveButtonColor="destructive"
      isValidForSubmit={isValidToSubmit}
      isSubmitting={isPending}
      submittingError={submittingError}
      hideDisplayButtonsOnSuccess
    >
      <DialogMessageBox
        id="delete-analytical-dataset-message-box"
        title={formatMessage({ id: 'analyticalDatasetsList.deleteModal.confirmTitle' })}
        type="error"
        variant="outlined"
        renderSubmitted={
          <Stack gap={4}>
            <Typography color="error">
              {formatMessage({ id: 'analyticalDatasetsList.deleteModal.actionUndone' })}
            </Typography>
            <Typography>
              {formatMessage(
                { id: 'analyticalDatasetsList.deleteModal.description' },
                { name: <b>{name}</b> }
              )}
            </Typography>
          </Stack>
        }
      />
    </ApiWorkflowDialog>
  );
};

export default DeleteAnalyticalDatasetDialog;
